import React, { useEffect, useMemo } from 'react'
import { LOCATION_SEARCH_ERROR_MESSAGE } from 'constants/search'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import { usePlacesBySearchPhrase } from 'hooks/usePlace'
import Caption from 'components/Luxkit/Typography/Caption'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import styled from 'styled-components'

const ResultContainer = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

interface Props {
  className?: string;
  searchPhrase?: string;
  searchTypes?: Array<App.SearchType>;
  placeTypes?: Array<App.SearchPlaceType>;
  onChange?: (item: App.SearchItem) => void;
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string) => void;
  vertical?: App.SearchPlaceVertical,
  overrideSearchItems?: Array<App.SearchItem>;
  selectedDropdown?: number;
}

function SearchPlaceSelectList(props: Props) {
  const {
    className,
    searchPhrase = '',
    onChange = noop,
    placeTypes,
    searchTypes,
    onResultsChange = noop,
    overrideSearchItems,
    vertical,
    selectedDropdown,
  } = props
  const [searchResults, loading] = usePlacesBySearchPhrase(searchPhrase, {
    disabled: !!overrideSearchItems,
    placeTypes,
    searchTypes,
    vertical,
  })

  useEffect(() => {
    onResultsChange(searchResults, searchPhrase)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults])

  const finalResults = useMemo(() => {
    if (!overrideSearchItems) {
      return searchResults
    }
    return overrideSearchItems.filter(result => {
      const normalisedSearch = searchPhrase.toLowerCase()
      return result.format.mainText.toLowerCase().includes(normalisedSearch) ||
        result.format.secondaryText?.toLowerCase().includes(normalisedSearch)
    })
  }, [overrideSearchItems, searchPhrase, searchResults])

  return <ResultContainer className={className}>
    <LoadingIndicator visible={loading} opaque floating>
      <BodyText variant="large" weight="bold" colour="neutral-one">Loading</BodyText>
    </LoadingIndicator>
    {searchPhrase.length > 0 && finalResults.length === 0 && !loading && <VerticalSpacer gap={4}>
      <BodyText variant="medium" colour="neutral-two">
        Sorry, we couldn't find <b>{searchPhrase}</b>.
      </BodyText>
      <Caption variant="medium" >{LOCATION_SEARCH_ERROR_MESSAGE}</Caption>
    </VerticalSpacer>}
    {searchPhrase.length > 0 && finalResults.map((searchItem, index) => <SearchMenuListItem
        key={`${searchItem.value}_${index}`}
        searchItem={searchItem}
        onClick={onChange}
        searchPhrase={searchPhrase}
        selected={index === selectedDropdown}
      />)}
  </ResultContainer>
}

export default SearchPlaceSelectList
